import React, { useMemo, useState } from 'react';
import ServiceBookabilitySummary from '../../../locations/components/ServiceBookabilitySummary';
import { stringToColor } from '../../../utils/avatarUtils';
import { normalizeLocationIds } from '../../../locations/locationTypes';
import {
  getStaffPhotoUrl,
  normalizeEmployeeIds,
} from './serviceStaffAssignmentUtils';
import '../../../components/clients/gallery/ClientGallery.css';
import './service-staff-assignment.css';

const normalize = (value) => String(value ?? '').trim();

const getInitials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  return (parts[0]?.[0] || '?').toUpperCase();
};

export default function ServiceStaffGalleryPicker({
  employeeIds = [],
  onChange,
  staffOptions = [],
  locationOptions = [],
  locationIds = [],
  disabled = false,
}) {
  const [search, setSearch] = useState('');

  const selectedSet = useMemo(
    () => new Set(normalizeEmployeeIds(employeeIds)),
    [employeeIds]
  );

  const displayedStaff = useMemo(() => {
    const query = normalize(search).toLowerCase();
    return staffOptions
      .filter((member) => {
        if (!query) return true;
        return String(member.name || '').toLowerCase().includes(query);
      })
      .sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
      );
  }, [staffOptions, search]);

  const normalizedLocationIds = useMemo(
    () => normalizeLocationIds(locationIds),
    [locationIds]
  );

  const toggleEmployee = (employeeId) => {
    const normalizedId = String(employeeId || '').trim();
    if (!normalizedId || disabled) return;

    const selectedIds = normalizeEmployeeIds(employeeIds);
    onChange?.(
      selectedIds.includes(normalizedId)
        ? selectedIds.filter((id) => id !== normalizedId)
        : [...selectedIds, normalizedId]
    );
  };

  const assignedCount = normalizeEmployeeIds(employeeIds).length;

  if (staffOptions.length === 0) {
    return (
      <div className="ssa-embedEmpty">
        Add staff members first to assign this service.
      </div>
    );
  }

  return (
    <div className="ssa-embed">
      <p className="ssa-embedSub">
        Tap an employee to assign or unassign them.
        {` ${assignedCount} of ${staffOptions.length} selected.`}
      </p>

      {assignedCount === 0 ? (
        <div className="ssa-warning" role="status">
          Assign at least one employee to make this service bookable.
        </div>
      ) : null}

      <input
        type="search"
        className="ssa-search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search by name…"
        disabled={disabled}
        aria-label="Search staff"
      />

      {displayedStaff.length === 0 ? (
        <div className="ssa-listEmpty">No staff match your search.</div>
      ) : (
        <div className="clientGallery ssa-gallery">
          <div className="clientGalleryGrid">
            {displayedStaff.map((staffMember) => {
              const memberId = String(staffMember.id || '');
              const selected = selectedSet.has(memberId);
              const photoUrl = getStaffPhotoUrl(staffMember);
              const displayName = staffMember.name || 'Team member';
              const initials = getInitials(displayName);
              const bgColor = stringToColor(displayName);

              return (
                <div
                  key={staffMember.id}
                  className={`clientGalleryItem ssa-galleryItem${selected ? ' ssa-galleryItem--selected' : ''}`}
                  onClick={() => toggleEmployee(staffMember.id)}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  aria-pressed={selected}
                  aria-label={`${selected ? 'Unassign' : 'Assign'} ${displayName}`}
                  onKeyDown={(event) => {
                    if (disabled) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleEmployee(staffMember.id);
                    }
                  }}
                >
                  <div className="ssa-galleryAvatarWrap">
                    <div className="clientGalleryImageWrapper">
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt=""
                          className="clientGalleryImage"
                          onError={(event) => {
                            event.currentTarget.style.display = 'none';
                            const fallback = event.currentTarget.nextElementSibling;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className="clientGalleryAvatar"
                        style={{
                          backgroundColor: bgColor,
                          display: photoUrl ? 'none' : 'flex',
                        }}
                      >
                        {initials}
                      </div>
                    </div>
                    {selected ? (
                      <span className="ssa-selectedMark" aria-hidden="true">
                        ✓
                      </span>
                    ) : null}
                  </div>
                  <div className="clientGalleryName">{displayName}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <ServiceBookabilitySummary
        staffOptions={staffOptions}
        locationOptions={locationOptions}
        employeeIds={employeeIds}
        locationIds={normalizedLocationIds}
      />
    </div>
  );
}
